// app/javascript/projects/components/filters/CodeModalFilterComponent.jsx

import React, { useState, useCallback, useEffect } from 'react';
import { ButtonToolbar } from 'ui';
import { useToasts } from 'ui/toast';
import useFilterData from 'hooks/useFilterData';
import CodesFilter from './CodesFilter';
import TranscriptsFilter from './TranscriptsFilter';
import DemographicFilters from './DemographicFilters';
import SurveysFilter from './SurveysFilter';
import AddFilterDropdownButton from './AddFilterDropdownButton';

// Filter type constants
const FILTER_TYPES = {
  CODES: 'codes',
  TRANSCRIPTS: 'transcripts',
  SURVEYS: 'surveys'
};

// Base available filter definitions (demographics added dynamically)
const BASE_FILTERS = [
  { id: FILTER_TYPES.CODES, name: 'Codes' },
  { id: FILTER_TYPES.TRANSCRIPTS, name: 'Transcripts' },
  { id: FILTER_TYPES.SURVEYS, name: 'Surveys' }
];

// Helper to create a demographic filter ID from descriptor ID
const getDemographicFilterId = (descriptorId) => `demographic_${descriptorId}`;

/**
 * Filter component for Code Modal with "Add Filter" dropdown pattern.
 *
 * - The main code is always included as a required filter (disabled in codes menu)
 * - Users can add additional filter types via the "Add Filter" dropdown
 * - When a filter is added, its dropdown opens automatically
 *
 * @param {Object} props
 * @param {string|number} props.projectID - The project ID
 * @param {string|number} props.requiredCodeId - The main code being viewed (always ANDed)
 * @param {Object} props.filters - Current filter state
 * @param {Function} props.onFilterChange - Callback when filters change
 * @param {Function} props.onApplyFilter - Callback to apply/invalidate filters
 */
const CodeModalFilterComponent = ({
  projectID,
  requiredCodeId,
  filters,
  onFilterChange,
  onApplyFilter,
  source = 'code_modal'
}) => {
  const { addToast } = useToasts();

  // Track which filter types are currently shown (empty by default - use Add Filter to add)
  const [activeFilterTypes, setActiveFilterTypes] = useState([]);
  // Track which filter was just added (to open its dropdown automatically)
  const [newlyAddedFilter, setNewlyAddedFilter] = useState(null);

  // Use the existing useFilterData hook
  const {
    codes,
    surveys,
    project,
    filterReport,
    descriptorsData,
    isLoading,
    error
  } = useFilterData(projectID, { ...filters, required_code_id: requiredCodeId }, source);

  // Handle errors with toast notification
  useEffect(() => {
    if (error) {
      addToast('Something went wrong loading filter data.', {
        appearance: 'error',
        autoDismiss: true
      });
    }
  }, [error, addToast]);

  // Get transcripts from project data
  const transcripts = project?.transcripts || [];

  // Normalize requiredCodeId to integer for comparison in callbacks
  const normalizedRequiredCodeIdForCallbacks = requiredCodeId ? parseInt(requiredCodeId, 10) : null;

  // Handler functions
  const toggleCode = useCallback((codeID) => {
    // Don't allow toggling the required code
    if (codeID === normalizedRequiredCodeIdForCallbacks) return;

    const currentCodes = filters.codes || [];
    const newCodes = currentCodes.includes(codeID)
      ? currentCodes.filter(id => id !== codeID)
      : [...currentCodes, codeID];

    onFilterChange({ ...filters, codes: newCodes });
  }, [filters, onFilterChange, normalizedRequiredCodeIdForCallbacks]);

  const toggleTranscript = useCallback((transcriptID) => {
    const currentTranscripts = filters.transcripts || [];
    const newTranscripts = currentTranscripts.includes(transcriptID)
      ? currentTranscripts.filter(id => id !== transcriptID)
      : [...currentTranscripts, transcriptID];

    onFilterChange({ ...filters, transcripts: newTranscripts });
  }, [filters, onFilterChange]);

  const toggleSurvey = useCallback((surveyID) => {
    const currentSurveys = filters.surveys || [];
    const newSurveys = currentSurveys.includes(surveyID)
      ? currentSurveys.filter(id => id !== surveyID)
      : [...currentSurveys, surveyID];

    onFilterChange({ ...filters, surveys: newSurveys });
  }, [filters, onFilterChange]);

  const toggleDemographic = useCallback((descriptorID, choiceID) => {
    const currentMultiChoices = { ...filters.multi_choices };

    if (!currentMultiChoices[descriptorID]) {
      currentMultiChoices[descriptorID] = [];
    }

    const choiceIndex = currentMultiChoices[descriptorID].indexOf(choiceID);

    if (choiceIndex >= 0) {
      currentMultiChoices[descriptorID] = currentMultiChoices[descriptorID].filter(id => id !== choiceID);
      if (currentMultiChoices[descriptorID].length === 0) {
        delete currentMultiChoices[descriptorID];
      }
    } else {
      currentMultiChoices[descriptorID] = [...currentMultiChoices[descriptorID], choiceID];
    }

    onFilterChange({ ...filters, multi_choices: currentMultiChoices });
  }, [filters, onFilterChange]);

  const clearCodes = useCallback(() => {
    // Keep the required code, clear others
    onFilterChange({ ...filters, codes: [] });
  }, [filters, onFilterChange]);

  const clearTranscripts = useCallback(() => {
    onFilterChange({ ...filters, transcripts: [] });
  }, [filters, onFilterChange]);

  const clearSurveys = useCallback(() => {
    onFilterChange({ ...filters, surveys: [] });
  }, [filters, onFilterChange]);

  const clearDemographic = useCallback((descriptorID) => {
    const newMultiChoices = { ...filters.multi_choices };

    if (descriptorID) {
      delete newMultiChoices[descriptorID];
    } else {
      // Clear all
      onFilterChange({ ...filters, multi_choices: {} });
      return;
    }

    onFilterChange({ ...filters, multi_choices: newMultiChoices });
  }, [filters, onFilterChange]);

  const setOperation = useCallback((op) => {
    onFilterChange({ ...filters, op });
  }, [filters, onFilterChange]);

  // Handle adding a new filter type
  const handleAddFilter = useCallback((filterId) => {
    if (!activeFilterTypes.includes(filterId)) {
      setActiveFilterTypes(prev => [...prev, filterId]);
      setNewlyAddedFilter(filterId);
    }
  }, [activeFilterTypes]);

  // Handle removing a filter type
  const handleRemoveFilter = useCallback((filterId) => {
    setActiveFilterTypes(prev => prev.filter(id => id !== filterId));

    // Clear the filter's data
    if (filterId === FILTER_TYPES.CODES) {
      clearCodes();
    } else if (filterId === FILTER_TYPES.TRANSCRIPTS) {
      clearTranscripts();
    } else if (filterId === FILTER_TYPES.SURVEYS) {
      clearSurveys();
    } else if (filterId.startsWith('demographic_')) {
      const descriptorId = parseInt(filterId.replace('demographic_', ''), 10);
      clearDemographic(descriptorId);
    }

    onApplyFilter();
  }, [clearCodes, clearTranscripts, clearSurveys, clearDemographic, onApplyFilter]);

  // Build available filters, including individual demographics
  const getAvailableFilters = () => {
    const available = [...BASE_FILTERS];

    // Remove surveys if none exist
    if (!surveys || surveys.length === 0) {
      const idx = available.findIndex(f => f.id === FILTER_TYPES.SURVEYS);
      if (idx >= 0) available.splice(idx, 1);
    }

    // Add individual demographics as separate filter options
    if (descriptorsData && descriptorsData.length > 0) {
      descriptorsData.forEach(descriptor => {
        available.push({
          id: getDemographicFilterId(descriptor.id),
          name: descriptor.name
        });
      });
    }

    return available;
  };

  if (isLoading) {
    return null;
  }

  // Normalize requiredCodeId to integer for comparison
  const normalizedRequiredCodeId = requiredCodeId ? parseInt(requiredCodeId, 10) : null;

  // Create a modified filters object that includes the required code in the codes array
  // This ensures the required code shows as checked in the CodesFilter dropdown
  const filtersWithRequiredCode = normalizedRequiredCodeId ? {
    ...filters,
    codes: filters.codes.includes(normalizedRequiredCodeId)
      ? filters.codes
      : [normalizedRequiredCodeId, ...filters.codes]
  } : filters;

  // Render a filter component based on its type
  const renderFilter = (filterId) => {
    if (filterId === FILTER_TYPES.CODES) {
      return (
        <CodesFilter
          key={filterId}
          codes={codes}
          filters={filtersWithRequiredCode}
          filterReport={filterReport}
          onToggleCode={toggleCode}
          onClearCodes={clearCodes}
          onSetOperation={setOperation}
          onApplyFilter={onApplyFilter}
          disabledCodeIds={normalizedRequiredCodeId ? [normalizedRequiredCodeId] : []}
          defaultOpen={newlyAddedFilter === FILTER_TYPES.CODES}
          onDeleteFilter={() => handleRemoveFilter(FILTER_TYPES.CODES)}
        />
      );
    }

    if (filterId === FILTER_TYPES.TRANSCRIPTS) {
      return (
        <TranscriptsFilter
          key={filterId}
          transcripts={transcripts}
          filters={filters}
          filterReport={filterReport}
          onToggleTranscript={toggleTranscript}
          onClearTranscripts={clearTranscripts}
          onApplyFilter={onApplyFilter}
          defaultOpen={newlyAddedFilter === FILTER_TYPES.TRANSCRIPTS}
          onDeleteFilter={() => handleRemoveFilter(FILTER_TYPES.TRANSCRIPTS)}
        />
      );
    }

    if (filterId === FILTER_TYPES.SURVEYS && surveys?.length > 0) {
      return (
        <SurveysFilter
          key={filterId}
          surveys={surveys}
          filters={filters}
          filterReport={filterReport}
          onToggleSurvey={toggleSurvey}
          onClearSurveys={clearSurveys}
          onApplyFilter={onApplyFilter}
          defaultOpen={newlyAddedFilter === FILTER_TYPES.SURVEYS}
          onDeleteFilter={() => handleRemoveFilter(FILTER_TYPES.SURVEYS)}
        />
      );
    }

    // Check if it's a demographic filter
    if (filterId.startsWith('demographic_')) {
      const descriptorId = parseInt(filterId.replace('demographic_', ''), 10);
      const descriptor = descriptorsData?.find(d => d.id === descriptorId);
      if (descriptor) {
        return (
          <DemographicFilters
            key={filterId}
            descriptorsData={[descriptor]}
            filters={filters}
            filterReport={filterReport}
            onToggleDemographic={toggleDemographic}
            onClearDemographic={clearDemographic}
            onApplyFilter={onApplyFilter}
            defaultOpen={newlyAddedFilter === filterId}
            onDeleteFilter={() => handleRemoveFilter(filterId)}
          />
        );
      }
    }

    return null;
  };

  return (
    <ButtonToolbar className='code-modal-filters' style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
      {/* Render filters in the order they were added */}
      {activeFilterTypes.map(filterId => renderFilter(filterId))}

      {/* Add Filter Dropdown */}
      <AddFilterDropdownButton
        availableFilters={getAvailableFilters()}
        activeFilterIds={activeFilterTypes}
        onAddFilter={handleAddFilter}
      />
    </ButtonToolbar>
  );
};

export default CodeModalFilterComponent;
